import { create } from "zustand";
import type {
  AckResult,
  SpectateDraft,
  TeamLobbyState,
  TeamLobbySummary,
  TeamRaceOver,
  TeamRaceState,
} from "@mk/shared";
import { getSocket } from "../../lib/socket.ts";
import { useSession } from "../../lib/session.ts";
import { recordCompletedGame } from "../../lib/adGate.ts";

export type TeamPhase = "idle" | "browser" | "lobby" | "countdown" | "live" | "over";

interface TeamState {
  phase: TeamPhase;
  lobbies: TeamLobbySummary[];
  lobby: TeamLobbyState | null;
  match: TeamRaceState | null;
  result: TeamRaceOver | null;
  spectating: boolean;
  exitingMatchId: string | null;
  setBrowser: () => void;
  setLobbies: (lobbies: TeamLobbySummary[]) => void;
  setLobby: (lobby: TeamLobbyState) => void;
  setMatch: (match: TeamRaceState) => void;
  setResult: (result: TeamRaceOver) => void;
  setPlayerDraft: (matchId: string, userId: string, draft: SpectateDraft | null) => void;
  setSpectatorCount: (matchId: string, count: number) => void;
  setRematchVotes: (matchId: string, votes: number) => void;
  exitSilently: (matchId: string | null) => void;
  reset: () => void;
}

export const useTeamStore = create<TeamState>((set, get) => ({
  phase: "idle",
  lobbies: [],
  lobby: null,
  match: null,
  result: null,
  spectating: false,
  exitingMatchId: null,
  setBrowser: () => set((state) => ({
    phase: state.match && state.phase !== "over" ? state.phase : "browser",
  })),
  setLobbies: (lobbies) => set({ lobbies }),
  setLobby: (lobby) => set((current) => {
    const meId = useSession.getState().user?.id;
    const meReturned = lobby.members.find((member) => member.user.id === meId)?.inLobby ?? false;
    return {
      lobby,
      phase:
        current.match && ["countdown", "live"].includes(current.phase)
          ? current.phase
          : current.phase === "over" && !meReturned
            ? "over"
            : "lobby",
    };
  }),
  setMatch: (match) => set((current) => current.exitingMatchId === match.matchId ? {} : ({
    match,
    result:
      match.phase === "over" && current.result?.matchId === match.matchId
        ? current.result
        : null,
    spectating: match.role === "spectator",
    exitingMatchId: null,
    phase: match.phase === "countdown" ? "countdown" : match.phase === "over" ? "over" : "live",
  })),
  setResult: (result) => {
    const current = get();
    if (!current.spectating && current.exitingMatchId !== result.matchId && result.reason !== "forfeit") {
      recordCompletedGame(`team:${result.matchId}`);
    }
    set((state) => state.exitingMatchId === result.matchId
      ? { exitingMatchId: null }
      : { result, phase: "over" });
  },
  /* Taslak artık tek başına geliyor; tuş başına tam maç durumu yayınlanmıyor. */
  setPlayerDraft: (matchId, userId, draft) => set((current) => {
    const match = current.match;
    if (match?.matchId !== matchId) return {};
    const players = match.players.map((player) =>
      player.user.id === userId ? { ...player, draft } : player,
    );
    return { match: { ...match, players } };
  }),
  setSpectatorCount: (matchId, count) => set((current) =>
    current.match?.matchId === matchId
      ? { match: { ...current.match, spectators: count } }
      : {},
  ),
  setRematchVotes: (matchId, votes) => set((current) =>
    current.result?.matchId === matchId
      ? { result: { ...current.result, rematchVotes: votes } }
      : {},
  ),
  exitSilently: (matchId) => set({
    phase: "idle",
    lobby: null,
    match: null,
    result: null,
    spectating: false,
    exitingMatchId: matchId,
  }),
  reset: () => set({ phase: "idle", lobbies: [], lobby: null, match: null, result: null, spectating: false, exitingMatchId: null }),
}));

let detach: (() => void) | null = null;

type TeamSocket = ReturnType<typeof getSocket>;

/** Arkadaş listesinden seçilen canlı 2v2 maçına izleyici olarak katılır. */
export function joinTeamSpectating(
  matchId: string,
  socket: TeamSocket = getSocket(),
): Promise<AckResult<TeamRaceState>> {
  return new Promise((resolve) => {
    socket.emit(
      "team:spectate:join",
      { matchId },
      (response: AckResult<TeamRaceState>) => {
        if (response.ok) useTeamStore.getState().setMatch(response.data);
        resolve(response);
      },
    );
  });
}

/** Socket yeniden bağlandığında açık 2v2 izleme üyeliğini sunucuda yeniden kurar. */
export function resumeTeamSpectating(socket: TeamSocket = getSocket()): void {
  const current = useTeamStore.getState();
  const match = current.match;
  if (match?.role !== "spectator" || !["countdown", "live", "over"].includes(current.phase)) {
    return;
  }

  const matchId = match.matchId;
  socket.emit(
    "team:spectate:join",
    { matchId, resume: true },
    (response: AckResult<TeamRaceState>) => {
      const latest = useTeamStore.getState();
      if (latest.exitingMatchId === matchId || latest.match?.matchId !== matchId) return;
      if (response.ok) {
        latest.setMatch(response.data);
      } else if (!latest.result) {
        latest.exitSilently(matchId);
      }
    },
  );
}

export function attachTeamListeners(socket: TeamSocket = getSocket()): () => void {
  if (detach) return detach;
  const store = useTeamStore.getState;
  const onLobbies = (payload: TeamLobbySummary[]) => store().setLobbies(payload);
  const onLobby = (payload: TeamLobbyState) => store().setLobby(payload);
  const onState = (payload: TeamRaceState) => store().setMatch(payload);
  const onFound = (payload: TeamRaceState) => store().setMatch(payload);
  const onOver = (payload: TeamRaceOver) => {
    store().setResult(payload);
    void useSession.getState().refreshStats();
  };
  const onClosed = (payload: { lobbyId: string }) => {
    if (store().lobby?.lobbyId === payload.lobbyId) store().reset();
  };
  const onVotes = (payload: { matchId: string; votes: number }) =>
    store().setRematchVotes(payload.matchId, payload.votes);
  const onDraft = (payload: { matchId: string; userId: string; draft: SpectateDraft | null }) =>
    store().setPlayerDraft(payload.matchId, payload.userId, payload.draft);
  const onWatchers = (payload: { matchId: string; count: number }) =>
    store().setSpectatorCount(payload.matchId, payload.count);
  const onConnect = () => resumeTeamSpectating(socket);

  socket.on("team:lobbies:update", onLobbies);
  socket.on("team:lobby:state", onLobby);
  socket.on("team:match:found", onFound);
  socket.on("team:match:state", onState);
  socket.on("team:match:over", onOver);
  socket.on("team:lobby:closed", onClosed);
  socket.on("team:rematch:votes", onVotes);
  socket.on("team:player:draft", onDraft);
  socket.on("team:watchers", onWatchers);
  socket.on("connect", onConnect);
  socket.emit("team:lobbies:sync", (response: AckResult<TeamLobbySummary[]>) => {
    if (response.ok) store().setLobbies(response.data);
  });

  detach = () => {
    socket.off("team:lobbies:update", onLobbies);
    socket.off("team:lobby:state", onLobby);
    socket.off("team:match:found", onFound);
    socket.off("team:match:state", onState);
    socket.off("team:match:over", onOver);
    socket.off("team:lobby:closed", onClosed);
    socket.off("team:rematch:votes", onVotes);
    socket.off("team:player:draft", onDraft);
    socket.off("team:watchers", onWatchers);
    socket.off("connect", onConnect);
    detach = null;
  };
  return detach;
}
